import { useCallback, useEffect, useState } from 'react'
import { Link, Navigate, NavLink, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { ErrorDeApi } from '@nucleo/api/cliente'
import { confirmarSalida, useTrabajoSinGuardar } from '@nucleo/trabajoSinGuardar'
import { actualizarAlcance, obtenerCatalogo, obtenerMatriz } from './clienteDeMatrices'
import { ETAPAS_DE_LA_MATRIZ, ETIQUETA_DE_ESTADO_DE_ETAPA, GUIA_DE_VISTA, rutaSugerida } from './etapas'
import { alcanceDesdeMatriz, FormularioDeAlcance } from './FormularioDeAlcance'
import { limpiar } from './NuevaMatriz'
import { PasoLevantamiento } from './PasoLevantamiento'
import { PasoMatriz } from './PasoMatriz'
import { PanelDeMatriz } from './PanelDeMatriz'
import { CargaDeLogo, LogoDeEmpresa } from './RecursosDeMatriz'
import { PasoOficializar } from './PasoOficializar'
import { PlanDeMedidas } from './PlanDeMedidas'
import type { Catalogo, Matriz } from './tipos'

const ICONO_GUIA = 'M12 17h.01M9.1 9a3 3 0 1 1 5.8 1c-.7 1.3-2.9 1.8-2.9 3.5M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20Z'
const ICONO_LISTO = 'M20 6 9 17l-5-5'

export function PaginaMatriz(props: { alSesionInvalida: () => void }) {
  const { alSesionInvalida } = props
  const { id = '' } = useParams()
  const ubicacion = useLocation()
  const [matriz, setMatriz] = useState<Matriz | null>(null)
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [versionLogo, setVersionLogo] = useState(0)

  const recargar = useCallback(async () => {
    try {
      setMatriz(await obtenerMatriz(id))
    } catch (e) {
      if (e instanceof ErrorDeApi && e.estado === 401) alSesionInvalida()
      else setError(e instanceof ErrorDeApi && e.estado === 404 ? 'Esta matriz no existe o no pertenece a tu organización.' : 'No fue posible cargar la matriz. Revisa tu conexión y vuelve a intentarlo.')
    }
  }, [id, alSesionInvalida])

  useEffect(() => {
    void recargar()
    obtenerCatalogo()
      .then(setCatalogo)
      .catch(() => setError('No fue posible cargar el catálogo de riesgos.'))
  }, [recargar])

  if (error) {
    return (
      <div role="alert" className="sst-vacio">
        <h2>No pudimos abrir la matriz</h2>
        <p>{error}</p>
        <Link to="/" className="sst-cta">Volver a las matrices</Link>
      </div>
    )
  }
  if (!matriz || !catalogo) {
    return <p role="status" className="inline-flex items-center gap-2 text-texto-secundario"><span className="aro-de-espera" aria-hidden="true" />Cargando la matriz…</p>
  }

  const ruta = `/matrices/${matriz.id}`
  const vista = ubicacion.pathname.slice(ruta.length + 1).split('/')[0] || 'panel'
  const clave = (vista in GUIA_DE_VISTA ? vista : 'panel') as keyof typeof GUIA_DE_VISTA
  const guia = GUIA_DE_VISTA[clave]
  const etapaActual = ETAPAS_DE_LA_MATRIZ.find((e) => e.ruta === clave)
  const guiaCompletada = clave === 'panel' ? matriz.estado === 'oficial' : etapaActual?.estado(matriz) === 'completo' && clave !== 'alcance'
  const filasActivas = matriz.filas.filter((f) => f.estado !== 'descartada').length
  const tareasActivas = matriz.tareas.filter((t) => t.estado !== 'descartada').length
  const protegerSalida = (evento: { preventDefault: () => void }) => { if (!confirmarSalida()) evento.preventDefault() }

  return (
    <section className="grid gap-4" aria-labelledby="titulo-matriz">
      <nav aria-label="Migas" className="text-pequeno text-texto-secundario">
        <Link to="/" className="text-accion underline-offset-2 hover:underline" onClick={protegerSalida}>Matrices</Link> <span aria-hidden="true">/</span> {matriz.alcance}
      </nav>

      <header className="sst-tarjeta sst-caso-cabecera">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <LogoDeEmpresa matrizId={matriz.id} version={versionLogo} />
            <div className="min-w-0">
              <p className="cs-eyebrow">{matriz.empresa} · {matriz.centro}</p>
              <h1 id="titulo-matriz">{matriz.alcance}</h1>
            </div>
          </div>
          <span className={`cs-state ${matriz.estado === 'oficial' ? 'cs-state--live' : 'cs-state--soon'}`}>
            {matriz.estado === 'oficial' ? `Matriz oficial · revisión ${matriz.revision_actual}` : matriz.revision_actual > 0 ? `Borrador de la revisión ${matriz.revision_actual + 1}` : 'Borrador'}
          </span>
        </div>
        <ul className="sst-meta" aria-label="Resumen de la matriz">
          <li>Tareas con visto bueno: {matriz.progreso.tareas_validadas} de {tareasActivas}</li>
          <li>Filas con visto bueno: {matriz.progreso.filas_validadas} de {filasActivas}</li>
          {matriz.progreso.filas_propuestas > 0 ? <li>Por revisar: {matriz.progreso.filas_propuestas} fila(s)</li> : null}
          <li>Responsable: {matriz.alcance_detalle.responsable.nombre}</li>
        </ul>
      </header>

      <nav aria-label="Etapas de la matriz" className="grid gap-3">
        <div>
          <NavLink to={`${ruta}/panel`} className="sst-pestana-general" onClick={protegerSalida}>
            <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z" /></svg>
            Vista general
          </NavLink>
        </div>
        <ol className="sst-etapas" data-orientacion="horizontal">
          {ETAPAS_DE_LA_MATRIZ.map((etapa) => {
            const estado = etapa.estado(matriz)
            const completada = estado === 'completo'
            return (
              <li key={etapa.ruta}>
                <NavLink
                  to={`${ruta}/${etapa.ruta}`}
                  className="sst-etapa"
                  data-completada={completada ? 'true' : undefined}
                  data-estado={estado}
                  aria-label={`Etapa ${etapa.numero}: ${etapa.titulo}, ${ETIQUETA_DE_ESTADO_DE_ETAPA[estado].toLowerCase()}`}
                  onClick={protegerSalida}
                >
                  <span className="sst-etapa-numero" aria-hidden="true">
                    {completada ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d={ICONO_LISTO} /></svg> : etapa.numero}
                  </span>
                  <span className="sst-etapa-textos" aria-hidden="true">
                    <span>{etapa.titulo}</span>
                    <span className="sst-etapa-estado">{ETIQUETA_DE_ESTADO_DE_ETAPA[estado]}</span>
                  </span>
                </NavLink>
              </li>
            )
          })}
        </ol>
      </nav>

      <NavLink to={`${ruta}/plan-medidas`} className="sst-pestana-general" onClick={protegerSalida}>
        Plan de medidas
      </NavLink>

      <section className="sst-guia" aria-labelledby="guia-de-vista" data-completada={guiaCompletada ? 'true' : undefined}>
        <span className="cs-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d={guiaCompletada ? ICONO_LISTO : ICONO_GUIA} /></svg>
        </span>
        <div className="grid gap-1">
          <h2 id="guia-de-vista" className="text-etiqueta uppercase text-marca">Qué hacer aquí · {guia.titulo}</h2>
          <p className="text-cuerpo">{guiaCompletada ? guia.completada : guia.que}</p>
          {!guiaCompletada ? <p className="text-pequeno text-texto-secundario">{guia.cierre}</p> : null}
        </div>
      </section>

      <Routes>
        <Route index element={<Navigate to="panel" replace />} />
        <Route path="panel" element={<PanelDeMatriz matriz={matriz} />} />
        <Route path="alcance" element={<PasoAlcance matriz={matriz} alActualizar={setMatriz} alCambiarLogo={() => setVersionLogo((n) => n + 1)} />} />
        <Route path="levantamiento" element={<PasoLevantamiento matriz={matriz} alActualizar={setMatriz} />} />
        <Route path="matriz" element={<PasoMatriz matriz={matriz} catalogo={catalogo} alActualizar={setMatriz} />} />
        <Route path="oficializar" element={<PasoOficializar matriz={matriz} alActualizar={setMatriz} />} />
        <Route path="plan-medidas" element={<PlanDeMedidas matriz={matriz} />} />
        <Route path="*" element={<Navigate to={rutaSugerida(matriz)} replace />} />
      </Routes>
    </section>
  )
}

function PasoAlcance(props: { matriz: Matriz; alActualizar: (m: Matriz) => void; alCambiarLogo: () => void }) {
  const [trabajando, setTrabajando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [guardado, setGuardado] = useState(false)
  const [modificado, setModificado] = useState(false)
  useTrabajoSinGuardar(modificado, 'El cambio en el alcance')
  return (
    <div className="grid gap-4">
      <div>
        <p className="cs-eyebrow">Etapa 1 · Alcance</p>
        <h2 className="text-h2">Entidad, centro de trabajo y participantes</h2>
      </div>
      <CargaDeLogo matrizId={props.matriz.id} alCambiar={props.alCambiarLogo} />
      {guardado ? <p role="status" className="rounded-md bg-conforme-fondo p-3 text-conforme-texto">Alcance guardado.</p> : null}
      <FormularioDeAlcance
        key={props.matriz.actualizada_en}
        inicial={alcanceDesdeMatriz(props.matriz)}
        textoDeEnvio="Guardar alcance"
        trabajando={trabajando}
        error={error}
        alModificar={(cambiado) => { setModificado(cambiado); if (cambiado) setGuardado(false) }}
        alEnviar={(a) => {
          setTrabajando(true)
          setError(null)
          setGuardado(false)
          actualizarAlcance(props.matriz.id, limpiar(a))
            .then((m) => {
              setModificado(false)
              props.alActualizar(m)
              setGuardado(true)
            })
            .catch((e: unknown) => setError(e instanceof ErrorDeApi ? e.message : 'No fue posible guardar el alcance. Tus cambios siguen en el formulario: vuelve a intentarlo.'))
            .finally(() => setTrabajando(false))
        }}
      />
    </div>
  )
}
